import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Descriptions,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  LinkOutlined,
  ReloadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import {
  AppotaPayReferActivationFlow,
  APPOTAPAY_PORTAL_URL,
} from '../../components/appotapay/AppotaPayReferActivationFlow';
import {
  fetchAppotaPayLookupHistory,
  fetchAppotaPaySubAccountDetail,
  linkAppotaPaySubAccount,
  lookupAppotaPayRefund,
  lookupAppotaPayTransaction,
  syncAppotaPaySubAccount,
  updateAppotaPaySubAccountStatus,
  type AppotaPaySubAccountDetail,
  type LookupResult,
  type SubAccountStatus,
} from '../../services/appotapaySubAccountsService';
import { formatCurrencyVND } from '../../utils/transactionHelpers';
import { formatVnDateTimeShort } from '../../utils/vnDateTime';

const { Title, Text } = Typography;

const CARD_STYLE: React.CSSProperties = {
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

function statusTag(status?: string) {
  switch (status) {
    case 'ACTIVE':
      return <Tag color="success">ACTIVE</Tag>;
    case 'SUSPENDED':
      return <Tag color="error">SUSPENDED</Tag>;
    case 'PENDING_ACTIVATION':
      return <Tag color="processing">PENDING_ACTIVATION</Tag>;
    default:
      return status ? <Tag>{status}</Tag> : '—';
  }
}

function JsonBlock({ value }: { value: unknown }) {
  if (value == null) return <Text type="secondary">Không có dữ liệu</Text>;
  return (
    <pre
      style={{
        maxHeight: 320,
        overflow: 'auto',
        background: '#fafafa',
        padding: 12,
        borderRadius: 8,
        fontSize: 12,
      }}
    >
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

export default function AppotaPaySubAccountDetailPage() {
  const { merchantId: merchantIdParam } = useParams<{ merchantId: string }>();
  const merchantId = parseInt(merchantIdParam || '', 10);
  const [searchParams, setSearchParams] = useSearchParams();

  const [detail, setDetail] = useState<AppotaPaySubAccountDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncLoading, setSyncLoading] = useState(false);

  const [linkOpen, setLinkOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [linkForm] = Form.useForm();
  const [statusForm] = Form.useForm();

  const [txRef, setTxRef] = useState('');
  const [txType, setTxType] = useState<'PARTNER_ORDER_ID' | 'TRANSACTION_ID'>(
    'PARTNER_ORDER_ID'
  );
  const [txResult, setTxResult] = useState<LookupResult | null>(null);
  const [txLoading, setTxLoading] = useState(false);

  const [refundRef, setRefundRef] = useState('');
  const [refundTxId, setRefundTxId] = useState('');
  const [refundResult, setRefundResult] = useState<LookupResult | null>(null);
  const [refundLoading, setRefundLoading] = useState(false);

  const [history, setHistory] = useState<
    Array<{
      id: number;
      lookupType: string;
      lookupKey: string;
      syncedAt: string;
      payload: unknown;
    }>
  >([]);

  const load = useCallback(
    async (refresh = false) => {
      if (!merchantId || Number.isNaN(merchantId)) return;
      setLoading(true);
      setError(null);
      try {
        const [data, hist] = await Promise.all([
          fetchAppotaPaySubAccountDetail(merchantId, refresh),
          fetchAppotaPayLookupHistory(merchantId, { limit: 20 }),
        ]);
        setDetail(data);
        setHistory(hist);
      } catch (e: unknown) {
        const ax = e as { response?: { data?: { message?: string } } };
        setError(ax.response?.data?.message || 'Không tải được chi tiết');
        setDetail(null);
      } finally {
        setLoading(false);
      }
    },
    [merchantId]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (searchParams.get('link') === '1') {
      setLinkOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('link');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSync = async () => {
    setSyncLoading(true);
    try {
      const data = await syncAppotaPaySubAccount(merchantId);
      setDetail(data);
      message.success('Đã đồng bộ từ AppotaPay');
      const hist = await fetchAppotaPayLookupHistory(merchantId, { limit: 20 });
      setHistory(hist);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Đồng bộ thất bại');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleLink = async (values: {
    referId: string;
    partnerCode: string;
    apiKey: string;
    secretKey: string;
    environment?: string;
    note?: string;
  }) => {
    try {
      await linkAppotaPaySubAccount(merchantId, values);
      message.success(
        'Đã kích hoạt tài khoản AppotaPay REFER — merchant có thể nhận thanh toán online'
      );
      setLinkOpen(false);
      linkForm.resetFields();
      await load(true);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Cập nhật thất bại');
    }
  };

  const handleStatus = async (values: {
    status: SubAccountStatus;
    note?: string;
  }) => {
    try {
      await updateAppotaPaySubAccountStatus(merchantId, values);
      message.success('Đã cập nhật trạng thái');
      setStatusOpen(false);
      await load();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Cập nhật thất bại');
    }
  };

  const handleTxLookup = async (force = false) => {
    if (!txRef.trim()) {
      message.warning('Nhập mã tham chiếu giao dịch');
      return;
    }
    setTxLoading(true);
    try {
      const res = await lookupAppotaPayTransaction(merchantId, {
        referenceId: txRef.trim(),
        type: txType,
        force,
      });
      setTxResult(res);
      message.success(`Tra cứu xong (nguồn: ${res.source || 'appotapay'})`);
      const hist = await fetchAppotaPayLookupHistory(merchantId, { limit: 20 });
      setHistory(hist);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Tra cứu thất bại');
    } finally {
      setTxLoading(false);
    }
  };

  const handleRefundLookup = async (force = false) => {
    if (!refundRef.trim() && !refundTxId.trim()) {
      message.warning('Nhập partnerRefId hoặc transactionId');
      return;
    }
    setRefundLoading(true);
    try {
      const res = await lookupAppotaPayRefund(merchantId, {
        partnerRefId: refundRef.trim() || undefined,
        transactionId: refundTxId.trim() || undefined,
        force,
      });
      setRefundResult(res);
      message.success(`Tra cứu hoàn tiền xong (nguồn: ${res.source})`);
      const hist = await fetchAppotaPayLookupHistory(merchantId, { limit: 20 });
      setHistory(hist);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Tra cứu hoàn tiền thất bại');
    } finally {
      setRefundLoading(false);
    }
  };

  const account = detail?.account;
  const awaitingCredentials =
    account?.status === 'PENDING_ACTIVATION' && !account?.hasCredentials;
  const showProvisionError = Boolean(
    account?.lastErrorCode && account.status !== 'PENDING_ACTIVATION'
  );

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Link to="/appotapay/merchants">
          <Button icon={<ArrowLeftOutlined />}>Danh sách</Button>
        </Link>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => load()}
          loading={loading}
        >
          Tải lại
        </Button>
        <Button
          type="primary"
          icon={<SyncOutlined />}
          onClick={handleSync}
          loading={syncLoading}
        >
          Đồng bộ AppotaPay
        </Button>
      </Space>

      <Title level={3} style={{ marginTop: 0 }}>
        Merchant AppotaPay #{merchantId}
        {account?.merchantName ? ` — ${account.merchantName}` : ''}
      </Title>

      {error && (
        <Alert type="error" message={error} style={{ marginBottom: 16 }} />
      )}

      {account && (
        <Card style={{ ...CARD_STYLE, marginBottom: 16 }}>
          <AppotaPayReferActivationFlow
            status={account.status}
            hasCredentials={account.hasCredentials}
            merchantId={merchantId}
            onEnterCredentials={() => setLinkOpen(true)}
          />
        </Card>
      )}

      {awaitingCredentials ? (
        <Card style={{ ...CARD_STYLE, marginBottom: 16 }}>
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              message="Bước tiếp theo: tạo REFER trên portal AppotaPay, rồi nhập credentials"
              description="AnGi không tự tạo merchant trên portal AppotaPay. Sau khi AppotaPay cấp bộ credentials, bấm nút bên dưới để nhập."
            />
            <Space wrap>
              <Button
                href={APPOTAPAY_PORTAL_URL}
                target="_blank"
                icon={<LinkOutlined />}
              >
                Mở portal AppotaPay (Sub Merchant)
              </Button>
              <Button type="primary" onClick={() => setLinkOpen(true)}>
                Nhập credentials AppotaPay (bước 3)
              </Button>
            </Space>
          </Space>
        </Card>
      ) : null}

      {account && account.status === 'ACTIVE' && account.hasCredentials ? (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          message="Merchant đã ACTIVE — sẵn sàng nhận thanh toán online và rút tiền qua REFER"
        />
      ) : null}

      {account && (
        <>
          <Card style={{ ...CARD_STYLE, marginBottom: 16 }} loading={loading}>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Merchant">
                <Link
                  to={`/merchants/${
                    account.merchantOwnerUserId ?? account.merchantId
                  }`}
                >
                  #{account.merchantId} {account.merchantName || ''}
                </Link>
              </Descriptions.Item>
              <Descriptions.Item label="Refer ID">
                {account.referId || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Credentials">
                {account.hasCredentials ? (
                  <Tag color="success">Đã có</Tag>
                ) : (
                  <Tag>Chưa có</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Môi trường">
                {account.environment || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Trạng thái">
                {statusTag(account.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Tên tài khoản">
                {account.accountName || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="SĐT / Email">
                {account.merchantPhone || '—'} · {account.merchantEmail || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Số dư AppotaPay (merchant)">
                {account.appotapayBalanceVnd != null
                  ? formatCurrencyVND(account.appotapayBalanceVnd)
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Có thể rút (nội bộ)">
                {formatCurrencyVND(detail?.withdrawableVnd ?? 0)}
              </Descriptions.Item>
              <Descriptions.Item label="Đồng bộ lúc">
                {account.appotapayLastSyncedAt
                  ? formatVnDateTimeShort(account.appotapayLastSyncedAt)
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Lỗi đồng bộ">
                {account.appotapaySyncError || '—'}
              </Descriptions.Item>
              {showProvisionError ? (
                <Descriptions.Item label="Lỗi kích hoạt">
                  {account.lastErrorCode
                    ? `${account.lastErrorCode}: ${
                        account.lastErrorMessage || ''
                      }`
                    : '—'}
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label="Ghi chú">
                {account.note || '—'}
              </Descriptions.Item>
            </Descriptions>

            <Space style={{ marginTop: 16 }} wrap>
              <Button type="primary" onClick={() => setLinkOpen(true)}>
                {account.hasCredentials
                  ? 'Cập nhật credentials AppotaPay'
                  : 'Nhập credentials AppotaPay'}
              </Button>
              <Button onClick={() => setStatusOpen(true)}>
                Đổi trạng thái
              </Button>
            </Space>
          </Card>

          <Collapse
            defaultActiveKey={awaitingCredentials ? [] : ['tools']}
            style={{ marginBottom: 16 }}
            items={[
              {
                key: 'tools',
                label: 'Tra cứu giao dịch & đồng bộ (nâng cao)',
                children: (
                  <>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} lg={12}>
                        <Card title="Tra cứu giao dịch" style={CARD_STYLE}>
                          <Space
                            direction="vertical"
                            style={{ width: '100%' }}
                            size={12}
                          >
                            <Input
                              placeholder="referenceId (mã đơn / partner ref)"
                              value={txRef}
                              onChange={e => setTxRef(e.target.value)}
                            />
                            <Select
                              value={txType}
                              style={{ width: '100%' }}
                              onChange={setTxType}
                              options={[
                                {
                                  value: 'PARTNER_ORDER_ID',
                                  label: 'PARTNER_ORDER_ID',
                                },
                                {
                                  value: 'TRANSACTION_ID',
                                  label: 'TRANSACTION_ID',
                                },
                              ]}
                            />
                            <Space>
                              <Button
                                type="primary"
                                loading={txLoading}
                                onClick={() => handleTxLookup(false)}
                              >
                                Tra cứu
                              </Button>
                              <Button
                                loading={txLoading}
                                onClick={() => handleTxLookup(true)}
                              >
                                Bỏ cache
                              </Button>
                            </Space>
                            {txResult && (
                              <>
                                <Text type="secondary">
                                  Nguồn: {txResult.source} · Trạng thái:{' '}
                                  {txResult.status || '—'}
                                </Text>
                                <JsonBlock value={txResult.data} />
                              </>
                            )}
                          </Space>
                        </Card>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Card title="Tra cứu hoàn tiền" style={CARD_STYLE}>
                          <Space
                            direction="vertical"
                            style={{ width: '100%' }}
                            size={12}
                          >
                            <Input
                              placeholder="partnerRefId hoàn tiền"
                              value={refundRef}
                              onChange={e => setRefundRef(e.target.value)}
                            />
                            <Input
                              placeholder="transactionId (tuỳ chọn)"
                              value={refundTxId}
                              onChange={e => setRefundTxId(e.target.value)}
                            />
                            <Space>
                              <Button
                                type="primary"
                                loading={refundLoading}
                                onClick={() => handleRefundLookup(false)}
                              >
                                Tra cứu
                              </Button>
                              <Button
                                loading={refundLoading}
                                onClick={() => handleRefundLookup(true)}
                              >
                                Bỏ cache
                              </Button>
                            </Space>
                            {refundResult && (
                              <>
                                <Text type="secondary">
                                  Nguồn: {refundResult.source} · Trạng thái:{' '}
                                  {refundResult.status || '—'}
                                </Text>
                                <JsonBlock value={refundResult.data} />
                              </>
                            )}
                          </Space>
                        </Card>
                      </Col>
                    </Row>

                    <Card
                      title="Thanh toán gần đây (DB)"
                      style={{ ...CARD_STYLE, marginTop: 16 }}
                    >
                      <Table
                        rowKey="id"
                        size="small"
                        pagination={false}
                        dataSource={detail.recentPayments || []}
                        columns={[
                          {
                            title: 'Reference',
                            dataIndex: 'referenceId',
                            ellipsis: true,
                          },
                          {
                            title: 'Transaction',
                            dataIndex: 'transactionId',
                            ellipsis: true,
                          },
                          { title: 'Kênh', dataIndex: 'channel', width: 90 },
                          {
                            title: 'Trạng thái',
                            dataIndex: 'status',
                            width: 100,
                          },
                          {
                            title: 'Số tiền',
                            dataIndex: 'amountVnd',
                            align: 'right',
                            render: v =>
                              v != null ? formatCurrencyVND(v) : '—',
                          },
                          {
                            title: 'Cập nhật',
                            dataIndex: 'updatedAt',
                            width: 140,
                            render: v => (v ? formatVnDateTimeShort(v) : '—'),
                          },
                        ]}
                      />
                    </Card>

                    <Card
                      title="Lịch sử tra cứu (DB cache)"
                      style={{ ...CARD_STYLE, marginTop: 16 }}
                    >
                      <Collapse
                        items={history.map(item => ({
                          key: String(item.id),
                          label: `${item.lookupType} · ${
                            item.lookupKey
                          } · ${formatVnDateTimeShort(item.syncedAt)}`,
                          children: <JsonBlock value={item.payload} />,
                        }))}
                      />
                    </Card>
                  </>
                ),
              },
            ]}
          />
        </>
      )}

      <Modal
        title="Nhập credentials AppotaPay (bước 3)"
        open={linkOpen}
        onCancel={() => setLinkOpen(false)}
        onOk={() => linkForm.submit()}
        okText="Lưu & kích hoạt ACTIVE"
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Nhập đúng bộ credentials AppotaPay gửi sau khi tạo Sub Merchant REFER"
        />
        <Form
          form={linkForm}
          layout="vertical"
          initialValues={{
            referId: account?.referId || '',
            environment: account?.environment || 'sandbox',
            note: account?.note || '',
          }}
          onFinish={handleLink}
        >
          <Form.Item
            name="referId"
            label="Refer ID"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="partnerCode"
            label="Partner Code"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="apiKey" label="API Key" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="secretKey"
            label="Secret Key"
            rules={[{ required: true }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item name="environment" label="Môi trường">
            <Select
              options={[
                { value: 'sandbox', label: 'Sandbox' },
                { value: 'production', label: 'Production' },
              ]}
            />
          </Form.Item>
          <Form.Item name="note" label="Ghi chú">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Đổi trạng thái tài khoản merchant"
        open={statusOpen}
        onCancel={() => setStatusOpen(false)}
        onOk={() => statusForm.submit()}
        destroyOnClose
      >
        <Form
          form={statusForm}
          layout="vertical"
          initialValues={{ status: account?.status, note: account?.note || '' }}
          onFinish={handleStatus}
        >
          <Form.Item
            name="status"
            label="Trạng thái"
            rules={[{ required: true }]}
          >
            <Select
              options={[
                { value: 'PENDING_ACTIVATION', label: 'PENDING_ACTIVATION' },
                { value: 'ACTIVE', label: 'ACTIVE' },
                { value: 'SUSPENDED', label: 'SUSPENDED' },
              ]}
            />
          </Form.Item>
          <Form.Item name="note" label="Ghi chú">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
